import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { OTP_LENGTH, PHONE_NUMBER_MASK } from '../../constants';
import type { OtpConfirmDto, OtpRequestDto } from '@api/models';
import { ModalResult } from '../../interfaces/modal-result.interface';
import { CustomerService } from '../../services/customer.service';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { OtpComponent } from '../otp/otp.component';
import { NgxMaskPipe } from 'ngx-mask';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';

@Component({
  selector: 'tc-confirm-phone-modal',
  templateUrl: './confirm-phone-modal.component.html',
  styleUrls: ['./confirm-phone-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslatePipe,
    OtpComponent,
    IconComponent,
    ReactiveFormsModule,
    ButtonComponent,
    NgxMaskPipe,
  ],
})
export class ConfirmPhoneModalComponent implements OnInit {
  dialogRef = inject(MatDialogRef<ConfirmPhoneModalComponent, ModalResult>);
  private destroyRef = inject(DestroyRef);
  customerService = inject(CustomerService);

  readonly otpDigitsFormArray = new FormArray(
    new Array(OTP_LENGTH).fill(null).map(() => new FormControl('', Validators.required)),
  );

  isLoading = signal<boolean>(false);
  error = signal<string>(null);
  state = signal<'title' | 'otp' | 'success'>('title');
  otpRequest = signal<OtpRequestDto>(null);

  ngOnInit(): void {
  }

  onSubmit(): void {
    this.error.set(null);

    switch (this.state()) {
      case 'title':
        this.confirmPhoneNumberInit();
        this.dialogRef.disableClose = true;
        break;

      case 'otp':
        this.isLoading.set(true);
        const otpConfirmDto: OtpConfirmDto = {
          requestId: this.otpRequest().requestId,
          otpCode: this.otpDigitsFormArray.getRawValue().join(''),
        };

        this.customerService.updatePhoneNumberConfirm(otpConfirmDto)
          .pipe(
            finalize(() => this.isLoading.set(false)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: () => {
              this.state.set('success');
              this.dialogRef.disableClose = false;
            },
            error: error => this.error.set(resolveHttpErrorMessage(error)),
          });
        break;

      case 'success':
        this.dialogRef.close({ type: 'CONFIRM' });
        break;
    }
  }

  confirmPhoneNumberInit(): void {
    this.error.set(null);

    this.isLoading.set(true);

    this.customerService.confirmPhoneNumberInit()
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.otpRequest.set(response);
          this.state.set('otp');
        },
        error: error => this.error.set(resolveHttpErrorMessage(error)),
      });
  }

  protected readonly PHONE_NUMBER_MASK = PHONE_NUMBER_MASK;
}
